import { isDateFamilyColumn } from '@thoughtspot/visualization-column-util';
import { ChartType, } from '../types';
import { getAttributeColumns, getChartType, getColumns, getMeasureColumns, getTimeFamilyColumns, } from './chart-model-utils';
import { getDimensionHandlingMappingForTSHostedCharts } from './chart-type-specification-service';
import { isMeasureNameColumn, isMeasureValueColumn, } from './measure-names-and-values';
const MAX_DEFAULT_MEASURE_AXIS_FIELDS = 2;
const MAX_DEFAULT_MEASURE_AXIS_FIELDS_FOR_LINE_COLUMN_CHART = 3;
const MAX_DEFAULT_ATTRIBUTE_AXIS_FIELDS = 1;
export const MIN_MEASURES_FOR_LINE_COLUMN_CHART = 2;
const MUZE_STACKED_CHARTS = [
    ChartType.AdvancedStackedArea,
    ChartType.AdvancedLineStackedColumn,
    ChartType.AdvancedStackedBar,
    ChartType.AdvancedStackedColumn,
    ChartType.AdvancedStackedArea,
];
export const DEFAULT_ADVANCED_CHART_MAPPING = [
    ['x-axis', 'x'],
    ['y-axis', 'y'],
    ['slice-with-color', 'color'],
    ['trellis-by', ''],
];
export const DEFAULT_ADVANCED_BAR_CHART_MAPPING = [
    ['x-axis', 'y'],
    ['y-axis', 'x'],
    ['slice-with-color', 'color'],
    ['trellis-by', ''],
];
export const DEFAULT_ADVANCED_LINE_COLUMN_CHART_MAPPING = [
    ['x-axis', 'x'],
    ['y-axis-line', 'y'],
    ['y-axis-column', 'y'],
    ['slice-with-color', 'color'],
    ['trellis-by', ''],
];
export const DEFAULT_ADVANCED_DONUT_CHART_MAPPING = [
    ['x-axis', 'x'],
    ['y-axis', 'y'],
    ['trellis-by', ''],
];
export const DEFAULT_ADVANCED_WATERFALL_CHART_MAPPING = [
    ['x-axis', 'x'],
    ['y-axis', 'y'],
    ['slice-with-color', 'color'],
    ['trellis-by', ''],
];
export const DEFAULT_ADVANCED_SCATTER_CHART_MAPPING = [
    ['x-axis', 'x'],
    ['y-axis', 'y'],
    ['slice', ''],
    ['slice-with-color', 'color'],
    ['trellis-by', ''],
];
export const DEFAULT_ADVANCED_BUBBLE_CHART_MAPPING = [
    ['x-axis', 'x'],
    ['y-axis', 'y'],
    ['slice', ''],
    ['slice-with-color', 'color'],
    ['size', ''],
    ['trellis-by', ''],
];
export const DEFAULT_CUSTOM_AXIS_CONFIG_TO_CUSTOM_CHART_CONFIG_TRANSFORMER = (customKey, columnIds) => {
    let columns = columnIds;
    if (customKey === 'x-axis') {
        columns = columnIds.slice(0, 1);
    }
    if (customKey === 'y-axis') {
        columns = columnIds.slice(0, MAX_DEFAULT_MEASURE_AXIS_FIELDS);
    }
    return generateAxisDrivenDimensionConfig(customKey, generateFlatAxisConfig(columns));
};
export const DEFAULT_CUSTOM_AXIS_CONFIG_TO_CUSTOM_CHART_CONFIG_TRANSFORMER_FOR_HORIZONTAL_CHARTS = (customKey, columnIds) => {
    let columns = columnIds;
    if (customKey === 'y-axis') {
        columns = columnIds.slice(0, 1);
    }
    if (customKey === 'x-axis') {
        columns = columnIds.slice(0, MAX_DEFAULT_MEASURE_AXIS_FIELDS);
    }
    return generateAxisDrivenDimensionConfig(customKey, generateFlatAxisConfig(columns));
};
export const DEFAULT_CUSTOM_AXIS_CONFIG_TO_CUSTOM_CHART_CONFIG_TRANSFORMER_FOR_LINE_COLUMN_CHART = (customKey, columnIds) => {
    let columns = columnIds;
    if (customKey === 'y-axis-column') {
        columns = columnIds.slice(0, 1);
    }
    else if (customKey === 'y-axis-line') {
        columns = columnIds.slice(1);
    }
    else {
        return generateAxisDrivenDimensionConfig(customKey, generateFlatAxisConfig(columnIds));
    }
    return generateAxisDrivenDimensionConfig(customKey, generateMergedAxisConfig(columns));
};
export const getColumnsfromCustomChartAxisConfig = (axisConfig) => {
    if (axisConfig.__typename === 'MergedAxis') {
        return axisConfig.columns;
    }
    if (axisConfig.__typename === 'DualAxis') {
        return [
            ...getColumnsfromCustomChartAxisConfig(axisConfig.primary),
            ...getColumnsfromCustomChartAxisConfig(axisConfig.secondary),
        ];
    }
    return axisConfig.column ? [axisConfig.column] : [];
};
export const getColumnsfromCustomChartDimension = (dimension) => {
    var _a, _b, _c, _d;
    if (dimension.__typename === 'AxisDrivenChartConfigDimension') {
        return ((_c = (_b = (_a = dimension.axes) === null || _a === void 0 ? void 0 : _a.map(axis => {
            return getColumnsfromCustomChartAxisConfig(axis);
        })) === null || _b === void 0 ? void 0 : _b.flat()) !== null && _c !== void 0 ? _c : []);
    }
    return (_d = dimension.columns) !== null && _d !== void 0 ? _d : [];
};
export const getColumnsfromCustomChartDimensions = (dimensions) => {
    return dimensions
        .map(dimension => getColumnsfromCustomChartDimension(dimension))
        .flat();
};
export const getCustomChartAxisColumns = (chartModel, axisKeyList) => {
    var _a, _b;
    let axisColumns = [];
    if ((_a = chartModel === null || chartModel === void 0 ? void 0 : chartModel.config) === null || _a === void 0 ? void 0 : _a.customChartConfig) {
        (_b = chartModel === null || chartModel === void 0 ? void 0 : chartModel.config) === null || _b === void 0 ? void 0 : _b.customChartConfig.forEach(config => {
            const dimensions = config.dimensions.filter(dim => axisKeyList.includes(dim.key));
            const dimensionColumns = getColumnsfromCustomChartDimensions(dimensions);
            const currentColumnIds = new Set(dimensionColumns);
            const currentColumns = chartModel.columns.filter(column => currentColumnIds.has(column.column.id));
            axisColumns = [...axisColumns, ...currentColumns];
        });
    }
    return axisColumns;
};
export const getCustomChartXAxisColumns = (chartModel) => {
    const configMap = getDimensionHandlingMappingForTSHostedCharts(getChartType(chartModel));
    const xAxisDimensionKeys = configMap.length
        ? configMap.filter(item => item[1] === 'x').map(item => item[0])
        : [];
    return getCustomChartAxisColumns(chartModel, xAxisDimensionKeys);
};
export const getCustomChartYAxisColumns = (chartModel) => {
    const configMap = getDimensionHandlingMappingForTSHostedCharts(getChartType(chartModel));
    const yAxisDimensionKeys = configMap.length
        ? configMap.filter(item => item[1] === 'y').map(item => item[0])
        : [];
    return getCustomChartAxisColumns(chartModel, yAxisDimensionKeys);
};
export const getCustomChartLegendColumns = (chartModel) => {
    const configMap = getDimensionHandlingMappingForTSHostedCharts(getChartType(chartModel));
    const legendAxisDimensionKeys = configMap.length
        ? configMap.filter(item => item[1] === 'color').map(item => item[0])
        : [];
    return getCustomChartAxisColumns(chartModel, legendAxisDimensionKeys);
};
export const getCustomChartRadialColumns = (chartModel) => {
    const configMap = getDimensionHandlingMappingForTSHostedCharts(getChartType(chartModel));
    const legendAxisDimensionKeys = configMap.length
        ? configMap.filter(item => item[1] === 'size').map(item => item[0])
        : [];
    return getCustomChartAxisColumns(chartModel, legendAxisDimensionKeys);
};
export const getCustomChartFieldColumns = (chartModel) => {
    return getCustomChartAxisColumns(chartModel, ['fields']);
};
export function generateFlatAxisConfig(columns) {
    return columns.map(column => ({
        __typename: 'FlatAxis',
        column,
    }));
}
export function generateMergedAxisConfig(columns) {
    return [
        {
            __typename: 'MergedAxis',
            columns,
        },
    ];
}
export function generateDualAxisConfig(primary, secondary) {
    return [
        {
            __typename: 'DualAxis',
            primary,
            secondary,
        },
    ];
}
export function generateAxisDrivenDimensionConfig(key, axes) {
    return {
        __typename: 'AxisDrivenChartConfigDimension',
        key,
        axes,
    };
}
export function generateColumnDrivenDimensionConfig(key, columns) {
    return {
        __typename: 'ColumnDrivenChartConfigDimension',
        key,
        columns,
    };
}
const generateDefaultCustomChartConfigForMuzeStudio = (chartViz) => {
    const cols = getColumns(chartViz);
    const dimensions = [
        generateColumnDrivenDimensionConfig('fields', cols
            .filter(c => !isMeasureNameColumn(c) && !isMeasureValueColumn(c))
            .map(c => c.id)),
    ];
    return [
        {
            __typename: 'CustomChartConfig',
            key: 'basic',
            dimensions,
        },
    ];
};
const generateDefaultCustomChartConfigForAdvancedPivotTable = (chartViz) => {
    var _a, _b;
    const cols = getColumns(chartViz);
    const measureCols = getMeasureColumns(chartViz).filter(column => !isMeasureValueColumn(column));
    const attributeCols = getAttributeColumns(chartViz).filter(column => !isMeasureNameColumn(column));
    const measureNameField = cols.find(isMeasureNameColumn);
    let rowFields = attributeCols.slice(0, 1);
    let valueFields = measureCols.slice(0, 1);
    let columnFields = measureNameField
        ? [measureNameField]
        : attributeCols.slice(1, 2);
    const currencyLinkedMeasureCols = measureCols.filter(c => { var _a, _b; return (_b = (_a = c.format) === null || _a === void 0 ? void 0 : _a.currencyFormat) === null || _b === void 0 ? void 0 : _b.column; });
    for (const col of currencyLinkedMeasureCols) {
        const currencyInfoColumnId = (_b = (_a = col === null || col === void 0 ? void 0 : col.format) === null || _a === void 0 ? void 0 : _a.currencyFormat) === null || _b === void 0 ? void 0 : _b.column;
        const currencyInfoColumn = cols.find(c => c.id === currencyInfoColumnId);
        const isCurrencyLinkedMeasureColPresent = valueFields.find(c => c.id === col.id);
        const isCurrencyInfoColumnPresent = rowFields.find(c => c.id === currencyInfoColumnId) ||
            columnFields.find(c => c.id === currencyInfoColumnId);
        if (isCurrencyLinkedMeasureColPresent &&
            currencyInfoColumn &&
            !isCurrencyInfoColumnPresent) {
            columnFields.push(currencyInfoColumn);
        }
    }
    if (attributeCols.length === 1 && !measureCols.length) {
        rowFields = [];
        valueFields = attributeCols.slice(0, 1);
        columnFields = [];
    }
    if (attributeCols.length > 1 && !measureCols.length) {
        rowFields = attributeCols.slice(1, 3);
        valueFields = attributeCols.slice(0, 1);
        columnFields = [];
    }
    if (measureCols.length && !attributeCols.length) {
        rowFields = measureNameField ? [measureNameField] : [];
        valueFields = measureCols.slice(0, 1);
        columnFields = [];
    }
    const dimensions = [
        generateColumnDrivenDimensionConfig('rows', rowFields.map(c => c.id)),
        generateColumnDrivenDimensionConfig('values', valueFields.map(c => c.id)),
        generateColumnDrivenDimensionConfig('columns', columnFields.map(c => c.id)),
    ];
    return [
        {
            __typename: 'CustomChartConfig',
            key: 'basic',
            dimensions,
        },
    ];
};
const getBaseColumnsForMuzeCartesianCharts = (chartViz, chartType, maxMeasureAxisFields = MAX_DEFAULT_MEASURE_AXIS_FIELDS) => {
    const measureCols = getMeasureColumns(chartViz).filter(column => !isMeasureValueColumn(column));
    const attributeCols = getAttributeColumns(chartViz).filter(column => !isMeasureNameColumn(column));
    const dateCols = getTimeFamilyColumns(chartViz).slice(0, 1);
    const nonDateAttributeCols = attributeCols.filter(c => !isDateFamilyColumn(c));
    const attrAxisFields = [...dateCols, ...nonDateAttributeCols].slice(0, MAX_DEFAULT_ATTRIBUTE_AXIS_FIELDS);
    const remainingAttributeCols = [...dateCols, ...nonDateAttributeCols].slice(MAX_DEFAULT_ATTRIBUTE_AXIS_FIELDS);
    const measureAxisFields = [...measureCols].slice(0, maxMeasureAxisFields);
    let sliceWithColorFields = [];
    const isStackedChart = MUZE_STACKED_CHARTS.includes(chartType);
    if (isStackedChart && remainingAttributeCols.length) {
        sliceWithColorFields = remainingAttributeCols.slice(0, 1);
    }
    return {
        attrAxisFields,
        measureAxisFields,
        sliceWithColorFields,
    };
};
const generateDefaultCustomChartConfigForLineColumnChart = (chartViz, chartType) => {
    const { attrAxisFields, measureAxisFields, sliceWithColorFields, } = getBaseColumnsForMuzeCartesianCharts(chartViz, chartType, MAX_DEFAULT_MEASURE_AXIS_FIELDS_FOR_LINE_COLUMN_CHART);
    const measureAxisFieldsIds = measureAxisFields.map(c => c.id);
    const attrAxisFieldsIds = attrAxisFields.map(c => c.id);
    const sliceWithColorFieldsIds = sliceWithColorFields.map(c => c.id);
    const attrFieldConfig = generateFlatAxisConfig(attrAxisFieldsIds);
    const primaryConfig = generateMergedAxisConfig(measureAxisFieldsIds.slice(0, 1));
    const secondaryConfig = generateMergedAxisConfig(measureAxisFieldsIds.slice(1));
    const dimensions = [
        generateAxisDrivenDimensionConfig('x-axis', attrFieldConfig),
        generateAxisDrivenDimensionConfig('y-axis-column', primaryConfig),
        generateAxisDrivenDimensionConfig('y-axis-line', secondaryConfig),
        generateAxisDrivenDimensionConfig('slice-with-color', generateFlatAxisConfig(sliceWithColorFieldsIds)),
    ];
    return [
        {
            __typename: 'CustomChartConfig',
            key: 'basic',
            dimensions,
        },
    ];
};
const generateDefaultCustomChartConfigForMuzeCharts = (chartViz, chartType) => {
    const { attrAxisFields, measureAxisFields, sliceWithColorFields, } = getBaseColumnsForMuzeCartesianCharts(chartViz, chartType);
    const isStackedChart = MUZE_STACKED_CHARTS.includes(chartType);
    const isHorizontalChart = chartType === ChartType.AdvancedBar ||
        chartType === ChartType.AdvancedStackedBar;
    let measureFieldConfig = [];
    const measureAxisFieldsIds = measureAxisFields.map(c => c.id);
    const attrAxisFieldsIds = attrAxisFields.map(c => c.id);
    const sliceWithColorFieldsIds = sliceWithColorFields.map(c => c.id);
    if (!isStackedChart) {
        if (measureAxisFields.length === 2) {
            measureFieldConfig =
                chartType === ChartType.AdvancedLine ||
                    chartType === ChartType.AdvancedArea
                    ? generateDualAxisConfig(generateFlatAxisConfig([measureAxisFieldsIds[0]])[0], generateFlatAxisConfig([measureAxisFieldsIds[1]])[0])
                    : generateMergedAxisConfig(measureAxisFieldsIds);
        }
        else {
            measureFieldConfig = generateFlatAxisConfig(measureAxisFieldsIds);
        }
    }
    else {
        if (measureAxisFields.length > 1 && !sliceWithColorFields.length) {
            measureFieldConfig = generateMergedAxisConfig(measureAxisFieldsIds);
        }
        else {
            measureFieldConfig = generateFlatAxisConfig(measureAxisFieldsIds);
        }
    }
    const attrFieldConfig = generateFlatAxisConfig(attrAxisFieldsIds);
    const dimensions = [
        generateAxisDrivenDimensionConfig('x-axis', isHorizontalChart ? measureFieldConfig : attrFieldConfig),
        generateAxisDrivenDimensionConfig('y-axis', isHorizontalChart ? attrFieldConfig : measureFieldConfig),
        generateAxisDrivenDimensionConfig('slice-with-color', generateFlatAxisConfig(sliceWithColorFieldsIds)),
    ];
    return [
        {
            __typename: 'CustomChartConfig',
            key: 'basic',
            dimensions,
        },
    ];
};
export const generateDefaultCustomChartConfigForTSHostedCharts = (chartViz, chartType) => {
    if (chartType === ChartType.MuzeStudio) {
        return generateDefaultCustomChartConfigForMuzeStudio(chartViz);
    }
    if (chartType === ChartType.AdvancedPivotTable) {
        return generateDefaultCustomChartConfigForAdvancedPivotTable(chartViz);
    }
    if (chartType === ChartType.AdvancedLineColumn ||
        chartType === ChartType.AdvancedLineStackedColumn) {
        return generateDefaultCustomChartConfigForLineColumnChart(chartViz, chartType);
    }
    return generateDefaultCustomChartConfigForMuzeCharts(chartViz, chartType);
};
//# sourceMappingURL=custom-chart-config-utils.js.map